import React, { useState, useEffect } from "react";
import Select from "react-select";
import { formatToZMW, rootUrl, sumOfArray } from "../../../helpers";
import { useOutletContext } from "react-router-dom";
import { useAuth } from "../../../context/auth-context";
import { FaList, FaSpinner, FaToggleOff, FaToggleOn, FaFilter, FaEye, FaEyeSlash, FaFileInvoice, FaMoneyBillWave } from "react-icons/fa";
import { FaBookJournalWhills } from "react-icons/fa6";

const CashBook = ({ startDate, endDate, triggerFetch }) => {
  const [allCash, setAllCash] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [openingBalance, setOpeningBalance] = useState(0);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [selectedCOA, setSelectedCOA] = useState(null); 
  const [selectedSOF, setSelectedSOF] = useState(null); // Add SOF filter state
  const [showDetailedView, setShowDetailedView] = useState(true);

  const [loading, setLoading] = useState(false)

  const {allData, setAllData} = useOutletContext();
  const auth = useAuth();


  const displayTable = (data, bf) => {
    let totalCredits = 0;
    let totalDebits = 0;
    let balance = bf;
    let bal = 0.0
    // Group data by COA
    const groupedData = data.reduce((acc, obj) => {
      const coa = allData.budget.find((a) => a.id === obj.coa_id)?.coa || "Unknown";
      if (!acc[coa]) acc[coa] = [];
      acc[coa].push(obj);
      return acc;
    }, {});
  
    const rows = [];
  
    Object.keys(groupedData).forEach((coa) => {
      let coaDebits = 0;
      let coaCredits = 0;
  
      // Calculate COA totals
      groupedData[coa].forEach((obj) => {
        const debit = obj.type === "income" ? parseFloat(obj.amount) : 0;
        const credit = obj.type === "expense" ? parseFloat(obj.amount) : 0;
        coaDebits += debit;
        coaCredits += credit;
        totalCredits += credit;
        totalDebits += debit;
        balance += debit - credit;
      });
  
      if (showDetailedView) {
        // Detailed transactions
        
        groupedData[coa].forEach((obj) => {
          const debit = obj.type === "income" ? parseFloat(obj.amount) : 0;
          const credit = obj.type === "expense" ? parseFloat(obj.amount) : 0;
           bal += (debit - credit)
          rows.push(
            <tr key={obj.id} className="border-b border-gray-200 hover:bg-blue-50 transition-colors">
              {/* Transaction details */}
              <td className="px-4 py-2.5 text-sm text-gray-700 font-medium">
                {allData.budget.find((a) => a.id === obj.coa_id)?.coa}
              </td>
              <td className="px-4 py-2.5 text-sm text-gray-700">{obj.date.substring(0, 10)}</td>
              <td className="px-4 py-2.5 text-sm text-gray-700">{obj?.voucher}</td>
              <td className="px-4 py-2.5 text-sm text-gray-700">{obj.cheque}</td>
              <td className="px-4 py-2.5 text-sm text-gray-700">
                {allData?.suppliers?.find(a => a.id == obj.paye)?.name 
                }
              </td>
              <td className="px-4 py-2.5 text-sm text-gray-600 max-w-xs truncate" title={obj.description}>{obj.description}</td>
              <td className="px-4 py-2.5 text-sm text-right text-green-700 font-medium">{formatToZMW(debit)}</td>
              <td className="px-4 py-2.5 text-sm text-right text-red-700 font-medium">{formatToZMW(credit)}</td>
              <td className="px-4 py-2.5 text-sm text-right text-gray-800 font-semibold">{formatToZMW(bal)}</td>
            </tr>
          );
        });
  
        // Subtotal row for detailed view
        rows.push(
          <tr key={`subtotal-${coa}`} className="bg-gray-100 border-t-2 border-gray-300">
            <td className="px-4 py-3 border-b border-gray-300 font-semibold text-left text-sm text-gray-800" colSpan="6">
              Subtotal for {coa} {allData.coa?.find(a=>a.account == coa)?.name}
            </td>
            <td className="px-4 py-3 border-b border-gray-300 font-semibold text-right text-sm text-gray-800">{formatToZMW(coaDebits)}</td>
            <td className="px-4 py-3 border-b border-gray-300 font-semibold text-right text-sm text-gray-800">{formatToZMW(coaCredits)}</td>
            <td className="px-4 py-3 border-b border-gray-300 font-semibold text-right text-sm text-gray-800">{formatToZMW(balance)}</td>
          </tr>
        );
      } else {
        // Summary view row
        rows.push(
          <tr key={`summary-${coa}`} className="bg-gray-50 border-b border-gray-200 hover:bg-gray-100 transition-colors">
            <td className="px-4 py-3 text-sm font-semibold text-gray-800">
              {coa} {allData.coa?.find(a=>a.account == coa)?.name}
            </td>
            <td className="px-4 py-3 text-sm font-semibold text-right text-gray-700">{formatToZMW(sumOfArray(allData.budget.filter(a=>a.coa == coa).map(res=>parseFloat(res.price))))}</td>
            <td className="px-4 py-3 text-sm font-semibold text-right text-green-700">{formatToZMW(coaDebits)}</td>
            <td className="px-4 py-3 text-sm font-semibold text-right text-red-700">{formatToZMW(coaCredits)}</td>
            <td className="px-4 py-3 text-sm font-semibold text-right text-gray-800">
              {formatToZMW(sumOfArray(allData.budget.filter(a=>a.coa == coa).map(res=>parseFloat(res.price))) - (coaDebits - coaCredits))}
            </td>
          </tr>
        );
      }
    });
  
    // Total row
    const totals = (
      <tr className="bg-gradient-to-r from-blue-950 to-blue-900 text-white border-t-4 border-blue-800">
        <th className="px-4 py-3 border-b border-blue-700 text-left font-bold text-base" colSpan={showDetailedView?6:1}>
          Grand Total
        </th>
        <th className="px-4 py-3 border-b border-blue-700 text-right font-bold text-base">{formatToZMW(sumOfArray(allData.budget.map(res=>parseFloat(res.price))))}</th>
        <th className="px-4 py-3 border-b border-blue-700 text-right font-bold text-base">{formatToZMW(totalDebits)}</th>
        <th className="px-4 py-3 border-b border-blue-700 text-right font-bold text-base">{formatToZMW(totalCredits)}</th>
        <th className="px-4 py-3 border-b border-blue-700 text-right font-bold text-base">{formatToZMW(sumOfArray(allData.budget.map(res=>parseFloat(res.price))) - (totalDebits - totalCredits))}</th>
      </tr>
    );
  
    return (
      <>
        {rows}
        {showDetailedView?'':totals}
      </>
    );
  };

  // Fetch data when triggerFetch changes (triggered by button in parent)
  useEffect(() => {
    if (triggerFetch && startDate && endDate) {
      const fetchData = async () => {
        setLoading(true);
        const response = await fetch(rootUrl(`/getCashByDate/${auth.user?.cid}~${startDate}~${endDate}`));
        const result = await response.json();
        const data = result.data || [];

        setAllCash(data.cash);
        const filtered = data.cash;
        setOpeningBalance(0);
        setFilteredData(filtered);
        setLoading(false);
      };
      fetchData();
    }
  }, [triggerFetch]);

  const handleFilter = () => {
    let filtered = allCash;
    
    // Filter by COA
    if (selectedCOA && selectedCOA.value) {
      // Find all budget entries with the selected COA code
      const matchingBudget = allData.budget.filter(a => a.coa === selectedCOA.value);
      const matchingIds = matchingBudget.map(a => a.id);
      filtered = filtered.filter((item) => matchingIds.includes(item.coa_id));
    }
    
    // Filter by SOF
    if (selectedSOF && selectedSOF.value) {
      filtered = filtered.filter((item) => item.sof == selectedSOF.value);
    }
    
    setFilteredData(filtered);
  };

  // Update COA options to show both code and name
  const coaOptions = [...new Set(allData.budget.map(res => res.coa))]
    .sort()
    .map((coa) => ({
      value: coa,
      label: `${coa} - ${allData.coa.find(c => c.account === coa)?.name || ''}`,
    }));

  // Create SOF options from allData.sof
  const sofOptions = allData.sof?.map((sof) => ({
    value: sof.id,
    label: sof.name,
  })) || [];

  return (
    <div className="w-full space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3 mb-4">
        <div className="p-2 bg-blue-950 rounded-lg">
          <FaBookJournalWhills className="text-white text-xl" />
        </div>
        <div>
          <h4 className="text-xl md:text-2xl text-gray-900 font-bold">Ledgers</h4>
          <p className="text-sm text-gray-500">Detailed ledger entries by COA</p>
        </div>
      </div>

      {/* Filters Section */}
      <div className="bg-gray-50 p-4 rounded-xl border border-gray-200">
        <div className="flex items-center gap-2 mb-4">
          <FaFilter className="text-gray-600" />
          <h3 className="text-sm font-semibold text-gray-700">Filters</h3>
        </div>
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
          <div className="flex items-center gap-2 flex-1">
            <FaFileInvoice className="text-gray-500 text-sm" />
            <Select
              options={coaOptions}
              value={selectedCOA}
              onChange={(selectedOption) => setSelectedCOA(selectedOption)}
              placeholder="Select COA"
              isClearable
              className="flex-1 text-sm"
            />
          </div>
          <div className="flex items-center gap-2 flex-1">
            <FaMoneyBillWave className="text-gray-500 text-sm" />
            <Select
              options={sofOptions}
              value={selectedSOF}
              onChange={(selectedOption) => setSelectedSOF(selectedOption)}
              placeholder="Select Source of Fund"
              isClearable
              className="flex-1 text-sm"
            />
          </div>
          <button 
            onClick={handleFilter} 
            className="px-5 py-2.5 bg-blue-950 text-white rounded-lg hover:bg-blue-900 transition-colors font-medium text-sm flex items-center gap-2 shadow-sm"
          >
            <FaFilter className="text-xs" />
            Filter
          </button>
          <button 
            onClick={() => setShowDetailedView(!showDetailedView)}
            className="px-5 py-2.5 bg-gray-700 text-white rounded-lg hover:bg-gray-800 transition-colors font-medium text-sm flex items-center gap-2 shadow-sm"
          >
            {showDetailedView ? (
              <>
                <FaEyeSlash className="text-xs" />
                <span className="hidden sm:inline">Summary View</span>
              </>
            ) : (
              <>
                <FaEye className="text-xs" />
                <span className="hidden sm:inline">Detailed View</span>
              </>
            )}
          </button>
        </div>
      </div>

      {/* Loading State */}
      {loading ? (
        <div className='flex flex-col justify-center items-center h-64 bg-gray-50 rounded-xl border border-gray-200'>
          <FaSpinner size={50} className="animate-spin text-blue-950 mb-4" />
          <p className="text-gray-600">Loading ledger data...</p>
        </div>
      ) : (
        <>
          {/* Table */}
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm">
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  {showDetailedView ? (
                    <tr className="bg-gradient-to-r from-blue-950 to-blue-900 text-white">
                      <th className="px-4 py-3 border-b border-blue-700 text-left font-semibold text-sm">COA</th>
                      <th className="px-4 py-3 border-b border-blue-700 text-left font-semibold text-sm">Date</th>
                      <th className="px-4 py-3 border-b border-blue-700 text-left font-semibold text-sm">Voucher</th>
                      <th className="px-4 py-3 border-b border-blue-700 text-left font-semibold text-sm">Cheque</th>
                      <th className="px-4 py-3 border-b border-blue-700 text-left font-semibold text-sm">Payee/Payer</th>
                      <th className="px-4 py-3 border-b border-blue-700 text-left font-semibold text-sm">Remarks</th>
                      <th className="px-4 py-3 border-b border-blue-700 text-right font-semibold text-sm">Debit</th>
                      <th className="px-4 py-3 border-b border-blue-700 text-right font-semibold text-sm">Credit</th>
                      <th className="px-4 py-3 border-b border-blue-700 text-right font-semibold text-sm">Balance</th>
                    </tr>
                  ) : (
                    <tr className="bg-gradient-to-r from-blue-950 to-blue-900 text-white">
                      <th className="px-4 py-3 border-b border-blue-700 text-left font-semibold text-sm">COA</th>
                      <th className="px-4 py-3 border-b border-blue-700 text-right font-semibold text-sm">Budgeted Amount</th>
                      <th className="px-4 py-3 border-b border-blue-700 text-right font-semibold text-sm">Debit</th>
                      <th className="px-4 py-3 border-b border-blue-700 text-right font-semibold text-sm">Credit</th>
                      <th className="px-4 py-3 border-b border-blue-700 text-right font-semibold text-sm">Balance</th>
                    </tr>
                  )}
                </thead>
                <tbody className="bg-white">{displayTable(filteredData, openingBalance)}</tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default CashBook;
